import { ReporterOptions, rudderStackReporter as rudderReporter } from '@cvent/sli-nextjs-metrics';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import getConfig from 'next/config';
import { getAnalyticsInstance } from '@hooks/useAnalytics';
import { getTransformer, Reporter } from './metricDataTransformers';

const LOG = LoggerFactory.create('RudderstackReporter');
const { publicRuntimeConfig } = getConfig();

/**
 * AH integrations handled by Rudderstack
 */
export const RUDDER_STACK_INTEGRATIONS = ['Mixpanel'] as const;

type ReporterData = Record<string, unknown> & {
  destinations: string[];
  id?: string;
  path?: string;
  downstreamName?: string;
};

type RudderstackReporterOptions = ReporterOptions & {
  metadata?: {
    analytics?: {
      id?: string;
      name?: string;
      path?: string;
    };
  };
};

/**
 * Rudderstack reporter, Overrides reporter from metrics' package to transform the data as needed
 * TODO HUB-100043 to cleanup the extra logic once all the analytics facts are migrated
 */
export const rudderStackReporter = (
  name: string,
  data: ReporterData | undefined = undefined,
  options?: RudderstackReporterOptions
) => {
  const { destinations } = data || {};
  // If the action has destinations, and not one supported by RS, skip
  if (destinations && !destinations.includes(...RUDDER_STACK_INTEGRATIONS)) {
    return;
  }
  // for Mixpanel we don't want to send the page_load events generated by the SLI package since the page views for
  // analytics are called manually via analytics.track({ path: '<PATH>', .... })
  if (name === 'page_load') {
    LOG.debug(`Skipping page_load event: ${JSON.stringify(data || '', null, 2)}`);
    return;
  }

  const analytics = getAnalyticsInstance();
  let dataCopy;
  let actionName;
  // if metadata and the downstream name is set, it's using the usePageActionsApi so we don't need to do anything extra
  if (options?.metadata?.analytics?.id && data?.downstreamName) {
    actionName = options.metadata.analytics.name;
    dataCopy = getTransformer(Reporter.RUDDERSTACK, options?.metadata?.analytics?.id)(data);
  } else if (analytics) {
    // If analytics has been initiated then we use it to transform the event name with the one from the analytics definitions
    // if the path is set in the data then we'll consider the action as a pageView and look for a definition based on
    // path; otherwise, the id will identify the definition
    const { path } = data;
    // We need to transform the event name with the one from the definition
    const definition = path ? analytics.pageViewFactDefinitions.get(path) : analytics.factDefinitions.get(name);
    actionName = definition?.name || name;
    dataCopy = {
      ...getTransformer(Reporter.RUDDERSTACK, definition?.id || name)(data),
      downstreamName: actionName
    };
  }

  if (publicRuntimeConfig.IS_DEV === 'true') {
    LOG.debug(`Mocked Event ${actionName} >>>> `, dataCopy);
    return;
  }

  rudderReporter(actionName, dataCopy);
};
